import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowBack, ArrowDownward, ArrowUpward, CalendarToday, Share, TouchApp, CheckCircle } from "@mui/icons-material";
import { baseURL } from "../../Constants";
import { getFantasyLeaderboard, getFantasyPlayerMatchesByTeamId, getFantasyTeamRanking, getFantasyTeams, getTeamDisqualification } from "../../services/FantasyService";
import { FantasyTeam } from "../../models/FantasyTeam";
import { FantasyPlayerMatch } from "../../models/FantasyPlayerMatch";
import PlayerSlotV2 from "../FantasyPage/PlayerSlotV2";
import Loading from "../../components/Loading";
import PlayerAvatar from "../../components/PlayerAvatar";
import { useBadges } from "../../services/BadgeService";
import { getSimpleDivisionLabel } from "../../utils/divisionUtils";
import { parseMatchTime } from "../../utils/matchTime";
import styles from "./Fantasy2.module.css";

type SortCol = 'time' | 'player' | 'playerPts' | 'rolePts' | 'teamPts' | 'total';

// The fantasy team view at /fantasy/view/:steamID/:season/:division.
// FUT cards come from the shared PlayerSlotV2.
const ViewFantasyTeamPage2 = () => {
    const { steamID, season, division } = useParams();
    const { t, i18n } = useTranslation(['pages', 'common']);
    const navigate = useNavigate();
    const badges = useBadges();

    const [team, setTeam] = useState<FantasyTeam | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [ranking, setRanking] = useState<number | null>(null);
    const [divisionTeamCount, setDivisionTeamCount] = useState<number | null>(null);
    const [matches, setMatches] = useState<FantasyPlayerMatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortCol, setSortCol] = useState<SortCol>('time');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [toast, setToast] = useState<string | null>(null);
    const [dqReason, setDqReason] = useState<string | null>(null);

    const seasonNum = Number(season);
    const locale = i18n.language === 'da' ? 'da-DK' : 'en-GB';

    useEffect(() => {
        if (!steamID || !season || !division) return;
        setLoading(true);
        setNotFound(false);
        setDqReason(null);
        (async () => {
            try {
                const teams = await getFantasyTeams(steamID);
                const found = teams.find((ft) => ft.season === seasonNum && ft.division === division) ?? null;
                setTeam(found);
                if (!found) {
                    setNotFound(true);
                    return;
                }
                // Ranking + division size + match log in parallel
                getFantasyTeamRanking(seasonNum, division, steamID).then(setRanking).catch(() => { });
                getFantasyLeaderboard(seasonNum, null)
                    .then((lb: any) => setDivisionTeamCount(lb?.[division]?.length ?? null))
                    .catch(() => { });
                getFantasyPlayerMatchesByTeamId(found.id)
                    .then((m) => setMatches(m ?? []))
                    .catch(() => setMatches([]));
                getTeamDisqualification(seasonNum, division, steamID)
                    .then((d) => setDqReason(d.disqualified ? d.reason : null))
                    .catch(() => { });
            } catch (err) {
                console.error("Error fetching fantasy team:", err);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [steamID, season, division, seasonNum]);

    const showToast = (text: string) => {
        setToast(text);
        window.setTimeout(() => setToast(null), 2400);
    };

    const handleShare = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            showToast(t('pages:fantasy.linkCopied'));
        } catch {
            showToast(t('pages:fantasy.copyError'));
        }
    };

    const playerNameOf = useCallback((steamId: string): string => {
        const player = team?.players.find((p) => p.account.steamID === steamId);
        return player?.account.playerName ?? steamId;
    }, [team]);

    const openMatch = async (m: { matchId: string; gameId: string }) => {
        try {
            // fantasy_player_matches stores the gameid in its matchid column, so resolve the real match id.
            let matchId = m.matchId;
            if ((!matchId || matchId === '0' || matchId === m.gameId) && m.gameId) {
                const response = await axios.get(`${baseURL}/Match/getMatchIdByGameId/${m.gameId}`);
                matchId = response.data?.matchId;
            }
            if (matchId) navigate(`/match/${matchId}`);
        } catch (err) {
            console.error("Error resolving match:", err);
        }
    };

    const sortBy = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(col);
            setSortDir(col === 'player' ? 'asc' : 'desc');
        }
    };

    const sortedMatches = useMemo(() => {
        const withTotal = matches.map((m) => ({
            ...m,
            total: m.playerPoints + m.rolePoints + m.teamPoints,
            timeValue: parseMatchTime(m.matchTime).getTime(),
            playerName: playerNameOf(m.steamId),
        }));
        return withTotal.sort((a, b) => {
            let cmp: number;
            switch (sortCol) {
                case 'time': cmp = a.timeValue - b.timeValue; break;
                case 'player': cmp = a.playerName.toLowerCase().localeCompare(b.playerName.toLowerCase(), locale); break;
                case 'playerPts': cmp = a.playerPoints - b.playerPoints; break;
                case 'rolePts': cmp = a.rolePoints - b.rolePoints; break;
                case 'teamPts': cmp = a.teamPoints - b.teamPoints; break;
                default: cmp = a.total - b.total;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [matches, sortCol, sortDir, playerNameOf, locale]);

    const formatMatchTime = (time: Date | string): string => {
        const d = parseMatchTime(time);
        return `${d.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
    };

    const budgetUsed = useMemo(() =>
        team ? team.players.reduce((acc, p) => acc + (p.price || 0), 0) : 0, [team]);

    const bestCard = useMemo(() => {
        if (!team || team.players.length === 0) return null;
        return team.players.reduce((best, p) => {
            const total = p.playerPoints + p.rolePoints + p.teamPoints;
            const bestTotal = best.playerPoints + best.rolePoints + best.teamPoints;
            return total > bestTotal ? p : best;
        });
    }, [team]);

    const columns: { key: SortCol; label: string; align?: 'center' }[] = [
        { key: 'time', label: t('pages:fantasy.colTime') },
        { key: 'player', label: t('pages:fantasy.colPlayerName') },
        { key: 'playerPts', label: t('pages:fantasy.colPlayer'), align: 'center' },
        { key: 'rolePts', label: t('pages:fantasy.colRole'), align: 'center' },
        { key: 'teamPts', label: t('pages:fantasy.colTeam'), align: 'center' },
        { key: 'total', label: t('pages:fantasy.colTotal'), align: 'center' },
    ];


    if (loading) {
        return <div className={styles.page}><div className="text-center" style={{ paddingTop: 60 }}><Loading /></div></div>;
    }

    if (notFound || !team) {
        return (
            <div className={styles.page}>
                <Link to="/fantasy" className={styles.backLink}>
                    <ArrowBack sx={{ fontSize: 18 }} />
                    {t('pages:fantasy.backToLeaderboard')}
                </Link>
                <p className={styles.emptyState}>{t('pages:fantasy.teamNotFound')}</p>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <Link to="/fantasy" className={styles.backLink}>
                <ArrowBack sx={{ fontSize: 18 }} />
                {t('pages:fantasy.backToLeaderboard')}
            </Link>

            {/* A disqualified team still has its page and its points - it is just hidden from the
                leaderboard. Say so here, otherwise the manager only sees that it "disappeared". */}
            {dqReason && (
                <div
                    role="status"
                    style={{
                        margin: '0 0 18px',
                        padding: '14px 16px',
                        borderRadius: 10,
                        border: '1px solid rgba(255, 107, 107, 0.45)',
                        background: 'rgba(255, 107, 107, 0.1)',
                        color: '#ffb3b3',
                        lineHeight: 1.5,
                    }}
                >
                    <strong style={{ display: 'block', marginBottom: 4, color: '#ff8a8a' }}>
                        {t('pages:fantasy.disqualifiedTitle')}
                    </strong>
                    {dqReason}
                    <div style={{ marginTop: 8 }}>
                        <Link to="/fantasy/guide" style={{ color: '#ffb3b3', textDecoration: 'underline' }}>
                            {t('pages:fantasy.disqualifiedGuideLink')}
                        </Link>
                    </div>
                </div>
            )}

            {/* ===== Header ===== */}
            <div className={styles.pageHeader}>
                <div className={styles.headerTop}>
                    <div className={styles.ownerHeading}>
                        <PlayerAvatar
                            steamId={team.account.steamID}
                            name={team.account.playerName}
                            size={46}
                            badges={badges}
                        />
                        <div>
                            <h1 className={styles.title}>{t('pages:fantasy.fantasyTeamTitle', { name: team.account.playerName })}</h1>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <CalendarToday sx={{ fontSize: 17, color: 'rgba(255,255,255,0.45)' }} />
                                    {t('common:seasonNum', { number: seasonNum })}
                                </span>
                                <span style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
                                <span>{division ? getSimpleDivisionLabel(division) : ''}</span>
                            </div>
                        </div>
                    </div>
                    <button className={styles.outlineButton} onClick={handleShare}>
                        <Share sx={{ fontSize: 18 }} />
                        {t('pages:fantasy.shareTeam')}
                    </button>
                </div>

                {/* Stat tiles */}
                <div className={styles.tileGrid}>
                    <div className={styles.tile}>
                        <div className={styles.tileLabel}>{t('pages:fantasy.placement')}</div>
                        <div className={styles.tileValueRow}>
                            <span className={`${styles.tileValue} ${styles.tileValueGold}`}>{ranking !== null ? `#${ranking}` : '–'}</span>
                            {divisionTeamCount !== null && (
                                <span className={styles.tileSub}>{t('pages:fantasy.ofTeams', { count: divisionTeamCount })}</span>
                            )}
                        </div>
                    </div>
                    <div className={styles.tile}>
                        <div className={styles.tileLabel}>{t('pages:fantasy.totalPoints')}</div>
                        <div className={styles.tileValue}>{team.points.toLocaleString('da-DK')}</div>
                    </div>
                    <div className={styles.tile}>
                        <div className={styles.tileLabel}>{t('pages:fantasy.budgetUsed')}</div>
                        <div className={styles.tileValueRow}>
                            <span className={styles.tileValue} style={{ fontSize: '1.5rem' }}>{budgetUsed.toLocaleString('da-DK')}</span>
                            <span className={styles.tileSub}>/ 1.000.000 kr.</span>
                        </div>
                    </div>
                    <div className={styles.tile}>
                        <div className={styles.tileLabel}>{t('pages:fantasy.bestCard')}</div>
                        <div className={styles.tileValueRow}>
                            <span className={styles.tileValueName}>{bestCard?.account.playerName ?? '–'}</span>
                            {bestCard && (
                                <span className={styles.tileValueSmall}>
                                    {bestCard.playerPoints + bestCard.rolePoints + bestCard.teamPoints}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== Squad ===== */}
            <div className={styles.squadHeader}>
                <h2 className={styles.sectionTitle} style={{ margin: 0 }}>{t('pages:fantasy.squad')}</h2>
                <span className={styles.flipHint}>
                    <TouchApp sx={{ fontSize: 16 }} />
                    {t('pages:fantasy.flipHint')}
                </span>
            </div>

            <div className={styles.squadRow}>
                {team.players.map((player) => {
                    const total = player.playerPoints + player.rolePoints + player.teamPoints;
                    const pct = (n: number) => total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0%';
                    return (
                        <div className={styles.squadCol} key={player.account.steamID}>
                            <PlayerSlotV2 player={player} isSelected locked />
                            <div className={styles.breakdownCard}>
                                <div className={styles.breakdownTop}>
                                    <span className={styles.breakdownLabel}>{t('pages:fantasy.colTotal')}</span>
                                    <span className={styles.breakdownTotal}>{total}</span>
                                </div>
                                <div className={styles.breakdownBar}>
                                    <div className={styles.breakdownPlayer} style={{ width: pct(player.playerPoints) }} />
                                    <div className={styles.breakdownRole} style={{ width: pct(player.rolePoints) }} />
                                    <div className={styles.breakdownTeam} style={{ width: pct(player.teamPoints) }} />
                                </div>
                                <div className={styles.breakdownValues}>
                                    <span><span style={{ color: '#f15c25' }}>●</span> {player.playerPoints}</span>
                                    <span><span style={{ color: '#ff9933' }}>●</span> {player.rolePoints}</span>
                                    <span><span style={{ color: '#c47a4a' }}>●</span> {player.teamPoints}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={styles.legend}>
                <span className={styles.legendItem}><span className={styles.legendSwatch} style={{ background: '#f15c25' }} />{t('pages:fantasy.playerPoints')}</span>
                <span className={styles.legendItem}><span className={styles.legendSwatch} style={{ background: '#ff9933' }} />{t('pages:fantasy.rolePoints')}</span>
                <span className={styles.legendItem}><span className={styles.legendSwatch} style={{ background: '#a05a30' }} />{t('pages:fantasy.teamPoints')}</span>
            </div>

            {/* ===== Match log ===== */}
            {sortedMatches.length > 0 && (
                <>
                    <div className={styles.divider} />
                    <h2 className={styles.sectionTitle} style={{ marginBottom: 18 }}>{t('pages:fantasy.detailedMatches')}</h2>
                    <div className={styles.table}>
                        <div className={`${styles.tableHeader} ${styles.gridMatches}`}>
                            {columns.map((col, idx) => (
                                <button
                                    key={col.key}
                                    className={`${styles.sortHeader} ${sortCol === col.key ? styles.sortHeaderActive : ''} ${idx >= 2 && idx <= 4 ? styles.gridMatchesHideSm : ''}`}
                                    style={{ justifyContent: col.align === 'center' ? 'center' : 'flex-start' }}
                                    onClick={() => sortBy(col.key)}
                                    title={t('pages:fantasy.sortHint')}
                                >
                                    {col.label}
                                    {sortCol === col.key && (sortDir === 'asc'
                                        ? <ArrowUpward sx={{ fontSize: 15 }} />
                                        : <ArrowDownward sx={{ fontSize: 15 }} />)}
                                </button>
                            ))}
                        </div>
                        {sortedMatches.map((m, i) => (
                            <div
                                className={`${styles.tableRow} ${styles.gridMatches} ${styles.matchRow} ${i % 2 === 0 ? styles.rowEven : styles.rowOdd}`}
                                key={`${m.gameId}-${m.steamId}-${i}`}
                                onClick={() => openMatch(m)}
                                title={t('pages:fantasy.openMatch')}
                            >
                                <div style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMatchTime(m.matchTime)}</div>
                                <div style={{ fontWeight: 500 }}>{m.playerName}</div>
                                <div className={`${styles.cellCenter} ${styles.gridMatchesHideSm}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{m.playerPoints}</div>
                                <div className={`${styles.cellCenter} ${styles.gridMatchesHideSm}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{m.rolePoints}</div>
                                <div className={`${styles.cellCenter} ${styles.gridMatchesHideSm}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{m.teamPoints}</div>
                                <div className={styles.cellCenter} style={{ color: '#ff9933', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{m.total}</div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Toast — portaled (the page fadeUp animation would trap position:fixed) */}
            {toast && createPortal(
                <div className={styles.toast}>
                    <CheckCircle className={styles.toastIcon} sx={{ fontSize: 20 }} />
                    <span className={styles.toastText}>{toast}</span>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ViewFantasyTeamPage2;
